import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RichTextEditor from "../components/RichTextEditor";
import FormModal from "../components/FormModal";
import api from "../api/axios";
import { useNotify } from "../context/NotificationContext";

export default function Faqs() {
  const { notify } = useNotify();

  const [faqs, setFaqs] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [serviceId, setServiceId] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  const loadFaqs = async () => {
    const r = await api.get("/faqs");
    setFaqs(r.data);
  };

  useEffect(() => {
    loadFaqs();
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  const openModal = (faq = null) => {
    setCurrent(faq);
    setServiceId(faq?.service_id || "");
    setQuestion(faq?.question || "");
    setAnswer(faq?.answer || "");
    setOpen(true);
  };
  const stripHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
  };
  const save = async () => {
    if (!serviceId || !question || !stripHtml(answer)) {
      notify("Please fill all fields", "warning");
      return;
    }

    const payload = { service_id: serviceId, question, answer };

    current
      ? await api.put(`/faqs/${current.id}`, payload)
      : await api.post("/faqs", payload);

    setOpen(false);
    notify("FAQ saved successfully");
    loadFaqs();
  };

  const requestDelete = (faq) => {
    setCurrent(faq);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    await api.delete(`/faqs/${current.id}`);
    setDeleteOpen(false);
    notify("FAQ deleted");
    loadFaqs();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          FAQs
        </Typography>
        <Typography color="text.secondary">
          Manage frequently asked questions per service
        </Typography>
      </Box>

      <Button variant="contained" sx={{ mb: 2 }} onClick={() => openModal()}>
        Add FAQ
      </Button>

      {/* Table */}
      <Table>
        <TableHead
          sx={{
            background: "#111827",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <TableRow>
            <TableCell width="200" sx={{ color: "#ffffff" }}>
              Service
            </TableCell>
            <TableCell sx={{ color: "#ffffff" }}>Question</TableCell>
            <TableCell width="120" align="right" sx={{ color: "#ffffff" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {faqs.map((f) => (
            <TableRow key={f.id} hover>
              <TableCell>{f.service?.name}</TableCell>
              <TableCell>{f.question}</TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton onClick={() => openModal(f)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" onClick={() => requestDelete(f)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}

          {faqs.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <Typography color="text.secondary">No FAQs found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Add/Edit Modal */}
      <FormModal
        open={open}
        title={current ? "Edit FAQ" : "Add FAQ"}
        onClose={() => setOpen(false)}
        onSubmit={save}
      >
        <TextField
          select
          fullWidth
          label="Service"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
          sx={{ mb: 2 }}
        >
          {services.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography fontWeight="bold" sx={{ mb: 1 }}>
          Answer
        </Typography>
        <RichTextEditor value={answer} onChange={setAnswer} />
      </FormModal>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{
          sx: {
            background: "#111827",
            color: "#fff",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>Are you sure you want to delete this FAQ?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
